import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, computed } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { MenuService } from 'src/app/services/menu.service';
import { initialsOf } from 'src/app/utils/account-menu';
import { AccountSheetComponent } from '../account-sheet/account-sheet.component';
import { APP_ROUTES } from 'src/app/types/app-routes';
import { SELLER_TOOLBAR_ITEMS } from '../dashboard-toolbar/dashboard-toolbar.configs';
import { NotificationBadgeService } from 'src/app/services/notification-badge.service';

/** A partir de esta cantidad el badge muestra "99+". */
export const MAX_BADGE_COUNT = 99;

/** Barra inferior cuyo primer ítem es el destino del logo del header. */
export type HeaderLogoToolbar = 'seller';

const LOGO_HOME_URL: Readonly<Record<HeaderLogoToolbar, string>> = {
  seller: SELLER_TOOLBAR_ITEMS[0].url,
};

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnChanges {
  @Input() title!: string;
  /** Segunda línea del título (pantallas de detalle: evento, fecha). Sin valor, el título queda en una línea como siempre. */
  @Input() subtitle: string | null = null;
  /**
   * Muestra el logo de TQR en lugar del título (que se conserva como texto oculto) y, al tocarlo, va
   * al primer ítem de la barra inferior indicada. Para las raíces que ya tienen esa barra.
   */
  @Input() logoFor: HeaderLogoToolbar | null = null;
  @Input() showBackButton: boolean = false;
  @Input() showSearchIcon: boolean = false;
  @Input() showFilterIcon: boolean = false;
  @Input() showHelpButton: boolean = false;
  @Input() showNotifications: boolean = false;
  /** El avatar de cuenta se oculta en las pantallas que SON la cuenta y dentro de un evento (lugar para el chip de rol). */
  @Input() showAccount: boolean = true;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() searchClicked = new EventEmitter<void>();
  @Output() filterClicked = new EventEmitter<void>();
  @Output() helpClicked = new EventEmitter<void>();
  @Output() notificationsClicked = new EventEmitter<void>();

  readonly hasUnread = computed(() => this.notificationBadgeService.unreadCount() > 0);

  readonly badgeLabel = computed(() => {
    const count = this.notificationBadgeService.unreadCount();
    return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count);
  });

  /** Clave de traducción del aria-label del botón de notificaciones: incluye
   *  la cantidad sin leer cuando corresponde, para que el lector de
   *  pantalla la anuncie (antes se perdía con un aria-label estático). */
  readonly notificationsLabelKey = computed(() =>
    this.hasUnread() ? 'header.notifications-unread' : 'header.notifications'
  );

  readonly notificationsLabelParams = computed(() => ({ count: this.badgeLabel() }));

  readonly initials = computed(() => initialsOf(this.menuService.userSignal()?.fullname));

  readonly isIOS: boolean;

  constructor(
    private readonly notificationBadgeService: NotificationBadgeService,
    private readonly menuService: MenuService,
    private readonly modalCtrl: ModalController,
    private readonly router: Router,
    platform: Platform
  ) {
    this.isIOS = platform.is('ios');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showNotifications']) {
      this.notificationBadgeService.setBellVisible(this.showNotifications);
    }
  }

  openAccount(): Promise<void> {
    return AccountSheetComponent.open(this.modalCtrl);
  }

  onLogoClick(): void {
    if (this.logoFor) {
      this.router.navigate([LOGO_HOME_URL[this.logoFor]]);
    }
  }

  close() {
    this.closeEvent.emit();
  }

  onSearchClick() {
    this.searchClicked.emit();
  }

  onFilterClick() {
    this.filterClicked.emit();
  }

  onHelpClick() {
    this.helpClicked.emit();
  }

  onNotificationsClick() {
    // Sin listener propio, la campana lleva sola al inbox (las raíces no repiten el handler).
    if (!this.notificationsClicked.observed) {
      this.router.navigate([APP_ROUTES.INBOX]);
      return;
    }
    this.notificationsClicked.emit();
  }

}
